import { useEffect, useId, useRef, useState } from 'react';
import { ChevronDown, Globe2, Info, Lock, Users } from 'lucide-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { PromptVisibility } from './promptsV2Types';
import {
  PROMPT_VISIBILITY_OPTIONS,
  promptVisibilityMeta,
  visibilityShortDescription,
  visibilityTooltipForItem,
} from '../../utils/promptVisibilityMeta';
import './VisibilitySelectDropdown.css';

type VisibilitySelectDropdownProps = {
  value: PromptVisibility;
  onChange: (next: PromptVisibility) => void;
  itemLabel?: string;
  label?: string;
  disabled?: boolean;
};

function OptionIcon({ tone }: { tone: 'public' | 'recruiters' | 'private' }) {
  if (tone === 'private') return <Lock size={16} aria-hidden />;
  if (tone === 'recruiters') return <Users size={16} aria-hidden />;
  return <Globe2 size={16} aria-hidden />;
}

export function VisibilitySelectDropdown({
  value,
  onChange,
  itemLabel = 'prompt',
  label = 'Privacy',
  disabled = false,
}: VisibilitySelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const selectedMeta = promptVisibilityMeta(value);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  return (
    <div className="visibility-select" ref={rootRef}>
      <span className="visibility-select__label">{label}</span>
      <div className="visibility-select__control">
        <button
          type="button"
          className="visibility-select__trigger"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          onClick={() => setOpen((prev) => !prev)}
        >
          <span
            className={`visibility-select__trigger-badge visibility-select__trigger-badge--${selectedMeta.tone}`}
          >
            <span className="visibility-select__trigger-icon" aria-hidden>
              <OptionIcon tone={selectedMeta.tone} />
            </span>
            <span className="visibility-select__trigger-text">{selectedMeta.label}</span>
          </span>
          <ChevronDown
            size={16}
            className={`visibility-select__chevron${open ? ' is-open' : ''}`}
            aria-hidden
          />
        </button>

        {open ? (
          <div id={listboxId} className="visibility-select__menu" role="listbox" aria-label={label}>
            {PROMPT_VISIBILITY_OPTIONS.map((option) => {
              const meta = promptVisibilityMeta(option);
              const isSelected = option === value;
              const description = visibilityShortDescription(option, itemLabel);
              const tooltip = visibilityTooltipForItem(option, itemLabel);

              return (
                <button
                  key={option}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={`visibility-select__option visibility-select__option--${meta.tone}${
                    isSelected ? ' is-selected' : ''
                  }`}
                  onClick={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  <span className="visibility-select__option-icon" aria-hidden>
                    <OptionIcon tone={meta.tone} />
                  </span>
                  <span className="visibility-select__option-copy">
                    <strong>{meta.label}</strong>
                    <span>{description}</span>
                  </span>
                  <OverlayTrigger
                    placement="top"
                    trigger={['hover', 'focus']}
                    overlay={
                      <Tooltip
                        id={`visibility-select-tip-${option}`}
                        className="prompt-visibility-tooltip"
                      >
                        {tooltip}
                      </Tooltip>
                    }
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      className="visibility-select__option-info"
                      aria-label={`About ${meta.label}`}
                      onClick={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.stopPropagation();
                        }
                      }}
                    >
                      <Info size={14} aria-hidden />
                    </span>
                  </OverlayTrigger>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
